// What a `/_session-bridge` page hands to its peer origin: the ACCESS token
// and its expiry — never the refresh token.
//
// WHY (2026-10-09): the server ROTATES refresh tokens (every /auth/refresh
// retires the token it was sent; no family revocation, a retired token gets
// 401 REVOKED). The bridge used to hand the peer the whole SDK session
// (collectSdkTokens), refresh token included. Shell and canvas then held one
// single-use refresh token; whichever origin refreshed second was signed out —
// about once per access-token lifetime for every user of both apps.
//
// Rule now: the origin that owns the login keeps its refresh token and is the
// only one that refreshes. The peer holds the access token only, and asks the
// bridge again when it runs out (sessionBridgeReacquire.js, wired into the SDK
// in sdkClient.js).
//
// A bridge page whose own access token has less than RENEW_WITHIN_MS left
// rotates it FIRST when it owns a refresh token, so a peer that re-asks never
// gets a token that dies in the next request. The window is wider than the
// peer's renewal lead (sessionBridgeRenewal.js, 2 min) and the SDK's own 60 s
// refresh buffer — otherwise a peer asking early gets the same dying token
// back. The rotation is the SDK's own (`tokenManager.refreshTokens()`,
// Web-Locks-serialised against this origin's other tabs); `getStoredAuthState`
// runs first only to await SDK init. A bridge page without a refresh token
// never refreshes; it hands over what it holds while that is still valid, or
// nothing.
//
// `getSdk` returns the page's SDK (or a promise of it) and is called — the SDK
// reached, on the light bridge entry even loaded — only when the rotation above
// is actually needed; every bridge surface passes it through the shared
// responder (sessionBridgeResponder.js). `sdk` is the eager form. With neither,
// the page answers from storage only.
//
// FA204 / frank trap — helpers stay inline in the export.

export const collectBridgeTokens = async ({ sdk, getSdk } = {}) => {
  const RENEW_WITHIN_MS = 5 * 60 * 1000
  const storage = (() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage ? localStorage : null
    } catch {
      return null
    }
  })()
  if (!storage) return null

  const jwtExpMs = (token) => {
    try {
      const part = String(token).split('.')[1]
      if (!part) return null
      let b64 = part.replace(/-/g, '+').replace(/_/g, '/')
      while (b64.length % 4) b64 += '='
      const json =
        typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('utf8')
      const exp = JSON.parse(json)?.exp
      return typeof exp === 'number' ? exp * 1000 : null
    } catch {
      return null
    }
  }
  const read = () => {
    const accessToken =
      storage.getItem('symbols_access_token') || storage.getItem('symbols_bridge_access_token')
    const stored = Number(
      storage.getItem('symbols_expires_at') || storage.getItem('symbols_bridge_expires_at')
    )
    const expiresAt = stored > 0 ? stored : accessToken ? jwtExpMs(accessToken) : null
    return { accessToken, expiresAt }
  }

  let session = read()
  const ownsRefresh = !!storage.getItem('symbols_refresh_token')
  const nearExpiry =
    !session.accessToken || (session.expiresAt && session.expiresAt - Date.now() < RENEW_WITHIN_MS)
  if (nearExpiry && ownsRefresh && (sdk || typeof getSdk === 'function')) {
    try {
      if (!sdk) sdk = await getSdk()
      // Awaits SDK init (the root `_tokenManager` is set at the end of it)
      // and recovers an already-expired token through the SDK's own path.
      if (typeof sdk.getStoredAuthState === 'function') await sdk.getStoredAuthState()
      session = read()
      const tm = sdk._tokenManager
      const stillNear =
        !session.accessToken ||
        (session.expiresAt && session.expiresAt - Date.now() < RENEW_WITHIN_MS)
      if (stillNear && tm?.hasRefreshToken?.() && typeof tm.refreshTokens === 'function') {
        await tm.refreshTokens()
      }
    } catch {
      /* refresh refused or unreachable — answer from what storage holds */
    }
    session = read()
  }

  if (!session.accessToken) return null
  if (session.expiresAt && session.expiresAt <= Date.now()) return null
  const out = { symbols_access_token: session.accessToken }
  if (session.expiresAt) out.symbols_expires_at = String(session.expiresAt)
  return out
}
